/**
 * Dashboard
 * Minimal logged-in home with active campaigns, "new campaign" entry point.
 * Uses SelfServeHeader + ShadCN Card, Badge, Progress, Button.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SelfServeHeader from '@/components/mediaplan/SelfServeHeader';
import ErrorCard from '@/components/mediaplan/ErrorCard';
import { DashboardSkeleton } from '@/components/mediaplan/ScreenSkeleton';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import {
  Plus,
  BarChart3,
  Calendar,
  Eye,
  Users,
  ArrowRight,
  Tv,
  ChevronRight,
} from 'lucide-react';
import mediaPlanApi from '@/services/mediaPlanApi';
import useMediaPlanFlow from '@/hooks/useMediaPlanFlow';

const POLL_INTERVAL = 30000; // 30 seconds

function StatusBadge({ status }) {
  const styles = {
    live: 'bg-teal/10 text-teal-700 border-teal/30',
    completed: 'bg-muted text-muted-foreground',
    generating: 'bg-coral/10 text-coral border-coral/30',
    review: 'bg-amber-50/50 text-amber-600 border-amber-100',
  };

  return (
    <Badge variant="outline" className={cn('text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1', styles[status] || styles.completed)}>
      {status === 'live' && <span className="w-1.5 h-1.5 rounded-full bg-teal animate-pulse" />}
      {status}
    </Badge>
  );
}

function CampaignCard({ campaign, onClick }) {
  return (
    <Card
      className="transition-all hover:shadow-md hover:border-muted-foreground/20 cursor-pointer group"
      onClick={onClick}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-sm font-bold">{campaign.name}</h3>
              <StatusBadge status={campaign.status} />
            </div>
            <div className="text-xs text-muted-foreground">{campaign.businessName}</div>
          </div>
          <div className="text-base font-bold">${campaign.budget.toLocaleString()}</div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Eye className="h-3 w-3 shrink-0" />
            <span>{campaign.impressions.toLocaleString()} views</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Users className="h-3 w-3 shrink-0" />
            <span>{campaign.reach.toLocaleString()} reached</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Calendar className="h-3 w-3 shrink-0" />
            <span>
              {new Date(campaign.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(campaign.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
          </div>
        </div>

        {/* Progress */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-medium text-muted-foreground">
              {campaign.status === 'live' ? `${campaign.daysRemaining} days remaining` : 'Campaign ended'}
            </span>
            <span className="text-[10px] font-semibold">{campaign.progress}%</span>
          </div>
          <Progress
            value={campaign.progress}
            className={cn(
              'h-1',
              campaign.status === 'live'
                ? '[&>[data-slot=progress-indicator]]:bg-gradient-to-r [&>[data-slot=progress-indicator]]:from-coral [&>[data-slot=progress-indicator]]:to-teal'
                : '[&>[data-slot=progress-indicator]]:bg-muted-foreground/30'
            )}
          />
        </div>

        {/* Arrow */}
        <div className="flex justify-end mt-3">
          <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-coral group-hover:translate-x-0.5 transition-all" />
        </div>
      </CardContent>
    </Card>
  );
}

export default function SelfServeDashboard() {
  const navigate = useNavigate();
  const flow = useMediaPlanFlow();

  const [campaigns, setCampaigns] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const businessName = flow.business?.name || 'Your Business';

  // Load campaigns
  async function loadCampaigns(isPolling = false) {
    if (!isPolling) {
      setLoading(true);
      setError(null);
    }
    try {
      const data = await mediaPlanApi.listCampaigns();
      setCampaigns(data);
      if (!isPolling) setError(null);
    } catch (err) {
      if (!isPolling) {
        setError(err.message || 'Failed to load campaigns');
      }
      // Silently ignore polling errors to avoid toast spam
    } finally {
      if (!isPolling) setLoading(false);
    }
  }

  useEffect(() => {
    loadCampaigns(false);

    // Poll every 30 seconds
    pollRef.current = setInterval(() => {
      loadCampaigns(true);
    }, POLL_INTERVAL);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Status-based navigation
  const handleCampaignClick = (campaign) => {
    switch (campaign.status) {
      case 'generating':
        navigate(`/campaign/${campaign.id}/generate`);
        break;
      case 'review':
        navigate(`/campaign/${campaign.id}/review`);
        break;
      case 'live':
      case 'completed':
      default:
        navigate(`/campaign/${campaign.id}/report`);
        break;
    }
  };

  const handleNewCampaign = () => {
    flow.reset();
    navigate('/campaign/new');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <SelfServeHeader businessName={businessName} />
        <div className="max-w-3xl mx-auto px-6 py-8">
          <DashboardSkeleton />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <SelfServeHeader businessName={businessName} />
        <ErrorCard
          message={error}
          onRetry={() => loadCampaigns(false)}
          fallbackPath="/campaign/new"
          fallbackLabel="Start a new campaign"
        />
      </div>
    );
  }

  const allCampaigns = campaigns || [];
  const liveCampaigns = allCampaigns.filter((c) => c.status === 'live');
  const pastCampaigns = allCampaigns.filter((c) => c.status !== 'live');

  return (
    <div className="min-h-screen bg-background">
      <SelfServeHeader businessName={businessName} />

      <div className="max-w-3xl mx-auto px-6 py-8">
        {/* Welcome */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold tracking-tight mb-1">Your Campaigns</h1>
            <p className="text-sm text-muted-foreground">Manage and track your CTV advertising campaigns.</p>
          </div>
          <Button
            className="bg-coral hover:bg-coral/90 text-white shadow-[0_2px_10px_rgba(255,107,107,0.35)] gap-1.5"
            onClick={handleNewCampaign}
          >
            <Plus className="h-4 w-4" />
            New Campaign
          </Button>
        </div>

        {/* Active campaigns */}
        {liveCampaigns.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 rounded-full bg-teal animate-pulse" />
              <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Active</h2>
            </div>
            <div className="space-y-3">
              {liveCampaigns.map((campaign) => (
                <CampaignCard
                  key={campaign.id}
                  campaign={campaign}
                  onClick={() => handleCampaignClick(campaign)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Past campaigns */}
        {pastCampaigns.length > 0 && (
          <div className="mb-8">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">Past Campaigns</h2>
            <div className="space-y-3">
              {pastCampaigns.map((campaign) => (
                <CampaignCard
                  key={campaign.id}
                  campaign={campaign}
                  onClick={() => handleCampaignClick(campaign)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {allCampaigns.length === 0 && (
          <div className="text-center py-20">
            <div className="w-16 h-16 rounded-2xl bg-coral/10 flex items-center justify-center mx-auto mb-4">
              <Tv className="h-8 w-8 text-coral" />
            </div>
            <h2 className="text-lg font-bold mb-2">No campaigns yet</h2>
            <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
              Get your business on streaming TV in minutes. We'll build your ad campaign from scratch.
            </p>
            <Button
              className="bg-coral hover:bg-coral/90 text-white shadow-[0_2px_10px_rgba(255,107,107,0.35)] gap-1.5"
              onClick={handleNewCampaign}
            >
              <Plus className="h-4 w-4" />
              Create Your First Campaign
            </Button>
          </div>
        )}

        {/* Quick actions */}
        {allCampaigns.length > 0 && (
          <Card className="bg-muted border-0 shadow-none">
            <CardContent className="p-5">
              <h3 className="text-sm font-semibold mb-3">Quick Actions</h3>
              <div className="grid grid-cols-2 gap-3">
                <Card
                  className="cursor-pointer hover:shadow-sm transition-all"
                  onClick={handleNewCampaign}
                >
                  <CardContent className="flex items-center gap-3 p-3">
                    <div className="w-9 h-9 rounded-lg bg-coral/10 flex items-center justify-center shrink-0">
                      <Plus className="h-4 w-4 text-coral" />
                    </div>
                    <div>
                      <div className="text-sm font-medium">New Campaign</div>
                      <div className="text-xs text-muted-foreground">Start a fresh ad campaign</div>
                    </div>
                  </CardContent>
                </Card>
                <Card
                  className="cursor-pointer hover:shadow-sm transition-all"
                  onClick={() => {
                    const first = allCampaigns[0];
                    if (first) navigate(`/campaign/${first.id}/report`);
                  }}
                >
                  <CardContent className="flex items-center gap-3 p-3">
                    <div className="w-9 h-9 rounded-lg bg-teal/10 flex items-center justify-center shrink-0">
                      <BarChart3 className="h-4 w-4 text-teal" />
                    </div>
                    <div>
                      <div className="text-sm font-medium">View Reports</div>
                      <div className="text-xs text-muted-foreground">Check campaign performance</div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
